import React, { useState } from 'react';

/**
 * ConfigurableInputField - A versatile input component with customizable styling and icons
 * 
 * @param {Object} props
 * @param {string} props.label - Label text displayed above the input (default: "Label")
 * @param {string} props.placeholder - Placeholder text for the input field (default: "Placeholder text")
 * @param {string} props.supportingText - Additional text shown below the input (default: "Supporting text")
 * @param {string} props.value - Initial value of the input field (default: "")
 * @param {string} props.size - Size variant of the input ('small', 'medium', 'large') (default: "large")
 * @param {boolean} props.showLabel - Whether to show the label above the input (default: true)
 * @param {boolean} props.showSupportingText - Whether to show supporting text below the input (default: true)
 * @param {boolean} props.showLeftIcon - Whether to show an icon on the left side of the input (default: true)
 * @param {boolean} props.showRightIcon - Whether to show an icon on the right side of the input (default: true)
 * @param {React.ReactNode} props.leftIcon - Custom icon component to display on the left (default: null)
 * @param {React.ReactNode} props.rightIcon - Custom icon component to display on the right (default: null)
 * @param {string} props.theme - Color theme for the input (default: "black")
 * @param {function} props.onChange - Callback function when input value changes (default: noop)
 * @param {string} props.type - Input type (e.g., 'text', 'password', 'email') (default: "text")
 * @param {boolean} props.disabled - Whether the input is disabled (default: false)
 * @param {boolean} props.error - Whether the input has an error state (default: false)
 * @param {string} props.errorText - Text to display when in error state (default: "Supporting text")
 * @param {string} props.className - Additional CSS classes for the container (default: "")
 * @param {boolean} props.fullWidth - Whether the input should use full width (default: false)
 * @returns {React.ReactElement} Rendered input field component
 */
const Label1 = ({
  // Field properties
  label = "Label",
  placeholder = "Placeholder text",
  supportingText = "Supporting text",
  value = "",
  size = "large", // 'small', 'medium', 'large'
  
  // Optional elements
  showLabel = true,
  showSupportingText = false,
  showLeftIcon = false,
  showRightIcon = false,
  
  // Custom icons
  leftIcon = null,
  rightIcon = null,
  
  // Theme options
  theme = "black", // 'black', 'blue', etc.
  
  // Additional props
  onChange = () => {},
  type = "text",
  disabled = false,
  error = false,
  errorText = "Supporting text",
  className = "",
  fullWidth = false, // Explicit prop for full width
}) => {
  const [inputState, setInputState] = useState("enabled"); // enabled, focused, hovered, empty
  const [inputValue, setInputValue] = useState(value);
  
  // Check if className contains width-related classes
  const hasWidthClass = className.includes('w-') || fullWidth;
  
  // Size mapping without width if className includes a width class
  const sizeClasses = {
    small: hasWidthClass ? "p-1 text-xs" : "w-48 p-1 text-xs",
    medium: hasWidthClass ? "p-1.5 text-sm" : "w-64 p-1.5 text-sm",
    large: hasWidthClass ? "p-2 text-sm" : "w-72 p-2 text-sm",
  };
  
  // State styling
  const getStateStyles = () => {
    if (disabled) return "bg-neutral-200 outline-neutral-200 cursor-not-allowed";
    if (error) return "bg-white outline-red-600";
    
    switch(inputState) {
      case "focused":
        return "bg-white outline-blue-600";
      case "hovered":
        return "bg-sky-100 outline-zinc-300";
      case "empty":
        return "bg-white outline-zinc-300";
      case "enabled":
      default:
        return "bg-white outline-zinc-300";
    }
  };

  // Text color based on state
  const getTextColor = () => {
    if (disabled) return "text-zinc-600";
    if (inputState === "empty") return "text-zinc-600";
    return "text-neutral-900";
  };
  
  // Handle states
  const handleFocus = () => {
    if (!disabled) setInputState("focused");
  };
  
  const handleBlur = () => {
    if (disabled) return;
    
    if (inputValue === "") {
      setInputState("empty");
    } else {
      setInputState("enabled");
    }
  };
  
  const handleMouseEnter = () => {
    if (disabled || inputState === "focused") return;
    setInputState("hovered");
  };
  
  const handleMouseLeave = () => {
    if (disabled || inputState === "focused") return;
    
    if (inputValue === "") {
      setInputState("empty");
    } else {
      setInputState("enabled");
    }
  };
  
  // Handle input change
  const handleChange = (e) => {
    if (disabled) return;
    
    setInputValue(e.target.value);
    onChange(e.target.value);
  };
  
  // Default icons
  const DefaultLeftIcon = () => (
    <div className="w-4 h-4 relative overflow-hidden">
      <div className={`w-2.5 h-2.5 left-[3.33px] top-[2.67px] absolute ${disabled ? "bg-zinc-600" : "bg-neutral-900"}`} />
    </div>
  );
  
  const DefaultRightIcon = () => (
    <div className="w-4 h-4 relative overflow-hidden">
      <div className="w-3 h-2 left-[2px] top-[4px] absolute bg-neutral-900 rounded-[1px]" />
    </div>
  );

  // Error icon for supporting text
  const ErrorIcon = () => (
    <div className="flex items-center justify-center w-5 h-5">
      <div className="w-3.5 h-3.5 rounded-full bg-red-600 flex items-center justify-center">
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 3L3 9M3 3L9 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  );
  
  return (
    <div className={`inline-flex flex-col justify-start items-start gap-0.5 ${className}`}>
      {/* Label */}
      {showLabel && (
        <div className="inline-flex justify-start items-start w-full">
          <div className="flex justify-start items-center gap-1">
            <div className="text-center justify-start text-neutral-900 text-base font-normal leading-normal">
              {label}
            </div>
          </div>
        </div>
      )}
      
      {/* Input field */}
      <div 
        className={`${sizeClasses[size]} rounded-lg outline outline-1 outline-offset-[-1px] ${getStateStyles()} inline-flex justify-start items-center gap-1 ${hasWidthClass ? "w-full" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Left icon - use custom if provided, otherwise default */}
        {showLeftIcon && (
          <div className="flex justify-center items-center ml-2">
            {leftIcon || <DefaultLeftIcon />}
          </div>
        )}
        
        {/* Input */}
        <input
          type={type}
          className={`flex-1 bg-transparent border-none outline-none ${getTextColor()} ${disabled ? "cursor-not-allowed" : ""}`}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
        />
        
        {/* Right icon - use custom if provided, otherwise default */}
        {showRightIcon && (
          <div className="flex justify-center items-center mr-2">
            {rightIcon || <DefaultRightIcon />}
          </div>
        )}
      </div>
      
      {/* Supporting text */}
      {showSupportingText && (
        <div className="inline-flex justify-start items-start w-full">
          <div className="flex justify-start items-center gap-0.5">
            {error && <ErrorIcon />}
            <div className={`text-center justify-start ${error ? "text-red-600" : "text-zinc-800"} text-xs leading-none`}>
              {error ? errorText : supportingText}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Label1;